import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/**
 * Minimal Leaflet map (free OSM tiles, no API key).
 * Uses HTML divIcons so there's no broken-marker-image webpack issue.
 * Swappable for Google Maps in Phase 4 behind this same component interface.
 *
 * props: center {lat,lng}, pickup {lat,lng}, vendors [{id,latitude,longitude,business_name,distance_km}],
 *        highlightVendorId, height
 */
const pin = (emoji, ring) =>
  L.divIcon({
    className: "avah-pin",
    html: `<div class="avah-pin__dot${ring ? " avah-pin__dot--ring" : ""}">${emoji}</div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
  });

export default function MapView({ center, pickup, vendors = [], highlightVendorId, height = 320 }) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    if (mapRef.current || !elRef.current) return;
    const c = center || pickup || { lat: 19.076, lng: 72.8777 };
    mapRef.current = L.map(elRef.current, { zoomControl: true, attributionControl: false })
      .setView([c.lat, c.lng], 13);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 })
      .addTo(mapRef.current);
    layerRef.current = L.layerGroup().addTo(mapRef.current);
    return () => { mapRef.current?.remove(); mapRef.current = null; };
  }, []); // eslint-disable-line

  useEffect(() => {
    const map = mapRef.current, group = layerRef.current;
    if (!map || !group) return;
    group.clearLayers();
    const bounds = [];

    if (pickup?.lat != null) {
      L.marker([pickup.lat, pickup.lng], { icon: pin("📍", true) })
        .bindTooltip("You are here", { direction: "top" }).addTo(group);
      bounds.push([pickup.lat, pickup.lng]);
    }
    vendors.forEach((v) => {
      if (v.latitude == null) return;
      const hot = String(v.id) === String(highlightVendorId);
      L.marker([+v.latitude, +v.longitude], { icon: pin(hot ? "🚨" : "🔧", hot) })
        .bindTooltip(`${v.business_name}${v.distance_km ? ` · ${v.distance_km} km` : ""}`, { direction: "top" })
        .addTo(group);
      bounds.push([+v.latitude, +v.longitude]);
    });

    if (bounds.length > 1) map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
    else if (bounds.length === 1) map.setView(bounds[0], 14);
    setTimeout(() => map.invalidateSize(), 60);
  }, [pickup, vendors, highlightVendorId]);

  return <div ref={elRef} style={{ height, width: "100%", borderRadius: 12 }} className="avah-map" />;
}
